import Vue from 'vue'
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex'
import axios from 'axios'
import moment from 'moment'
import Modernizr from 'modernizr'
import {Field, FieldDependency, FieldValue, initField, loadFields, loadSpecs, requirementPasses,} from '@/model/Field'
import {API_URL} from '@/config'
import * as fieldsConfig from '../../order-form-requirements.yml'
import {getColorWarnings} from '@/data/colors';
import {getCavettoHelpText} from '@/data/cavetto';

interface Inquiry {
  form_type: string,
  offer_number?: number,
  requisition_number?: number,
  material: Material,
  wood?: Wood,
  corian?: Corian,
  cooktop_mounting?: WithDescription<string>,
  cooktop_steel_rods?: {
    sort: string,
    side: Side,
  },
  wood_charger_count?: number,
  corian_charger_count?: number,
  el_tower_count: number,
  el_tower_type?: string,
  corian_backflip_count?: number,
  wood_backflip_count?: number,
  basin?: {
    sort: BasinSort,
    color: string,
    mounting: BasinMounting,
    mixer?: boolean,
    mixer_diameter?: number
  },
  customer: Customer,
  delivery: Delivery,
  retailer: Retailer,
  comments: string,
}

type Material = 'wood' | 'corian' | 'both';
type BasinMounting = 'drop_in' | 'undermount';
type CooktopMounting = 'drop_in' | 'level_glued';
type BasinSort = 'sink' | 'washbasin';
type Side = 'left' | 'right';

interface WithDescription<T> {
  value: T,
  description?: string,
}

interface Wood {
  genus: string,
  layout: 'finger_joined' | 'whole_staff',
  thickness: number,
  surface_treatment: 'osmo' | 'lin',
  color: string,
  custom_color?: string,
  profile: string,
}

interface Corian {
  thickness: number,
  color: {
    name: string,
    warnings?: string[],
  },
  profile: string,
  groove?: {
    sort: string,
    side: Side,
  },
  cavetto: {
    name: string,
    helptText?: string,
  },
  cavetto_height?: number,
}

interface Customer {
  name: string,
  phone?: string,
  delivery_phone?: string,
  email?: string,
  address?: string,
  postal_number: number,
  postal_place: string,
  floor: number,
  elevator?: boolean,
}

interface Delivery {
  hull_date?: string,
  countertop_date?: string,
  mounting: boolean,
  lift: boolean,
  helper: boolean,
  wall_seam?: string,
  measurements?: 'ready' | 'corinor' | {
    later: string, // date
  },
  parking_distance?: number,
  entrepreneur_name?: string,
}

interface Retailer {
  name: string,
  phone: string,
  email: string,
  retailer: string,
}

export const ORDER_NAMESPACE = 'orderForm'
const specs = loadSpecs(fieldsConfig)

export class FormState {
  fields: Field[] = loadFields(fieldsConfig)
  changed: boolean = false
  attachments: {
    countertop_drawing?: File,
    floor_plan?: File,
    perspective_drawing?: File,
  } = {}
}

const getters: GetterTree<FormState, any> = {
  field: state => (fieldName: string): Field | undefined => {
    return state.fields.find(field => field.name === fieldName)
  },
  changed: state => {
    return state.changed
  },
  show: (state, getters) => (fieldName: string): boolean => {
    let spec = specs.find(spec => spec.field === fieldName)
    if (!spec) {
      return true
    }

    if (spec.when === 'always') {
      return true
    } else {
      let checkDepencency = (dependency: FieldDependency) => {
        let dependencyField: Field | undefined = getters.field(dependency.field)
        if (!dependencyField || dependencyField.value === undefined) {
          return false
        }

        // Don't show field if a field it depends on is hidden.
        if (!getters.show(dependencyField.name)) {
          return false
        }

        return requirementPasses(dependency.requirement, dependencyField.value)
      }

      if (spec.when.type === 'either') {
        return spec.when.dependencies.some(checkDepencency)
      } else {
        return spec.when.dependencies.every(checkDepencency)
      }
    }
  },
  fieldValue: (state, getters) => (fieldName: string): FieldValue => {
    let field: Field | undefined = getters.field(fieldName)
    if (field) {
      return field.value
    } else {
      return undefined
    }
  },
  fieldValueMap: (state, getters): Map<string, FieldValue> => {
    let map = new Map()
    for (let field of state.fields) {
      if (getters.show(field.name)) {
        map.set(field.name, field.value)
      }
    }
    return map
  },
  inquiry: (state, getters): Inquiry => {
    const map = getters.fieldValueMap

    function material (): Material {
      return map.get('material');
    }

    function format_date (date?: string): string | undefined {
      if (date) {
        return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
      } else {
        return undefined
      }
    }

    return {
      form_type: map.get('form_type'),
      offer_number: parseInt(map.get('offer_num'), 10),
      requisition_number: parseInt(map.get('requisition_num'), 10),
      material: material(),
      wood: (() => {
        if (!['wood', 'both'].includes(map.get('material'))) {
          return undefined
        } else {
          return {
            genus: map.get('genus'),
            layout: map.get('wood_layout'),
            thickness: (() => {
              const val = map.get('wood_thickness')
              if (val === 'other') {
                return parseInt(map.get('wood_thickness_custom'), 10)
              } else {
                return parseInt(val, 10)
              }
            })(),
            surface_treatment: map.get('surface_treatment'),
            color: (() => {
              const treatment = map.get('surface_treatment')
              if (treatment === 'osmo') {
                const osmoColor = map.get('osmo_color')
                if (osmoColor === 'osmo_custom') {
                  return 'other'
                } else {
                  return osmoColor;
                }
              } else {
                return map.get('lin_color')
              }
            })(),
            custom_color: (() => {
              const treatment = map.get('surface_treatment')
              if (treatment === 'osmo') {
                const osmoColor = map.get('osmo_color')
                if (osmoColor === 'osmo_custom') {
                  return map.get('osmo_color_custom')
                } else {
                  return undefined;
                }
              } else {
                return undefined
              }
            })(),
            profile: map.get('wood_profile'),
          }
        }
      })(),
      corian: (() => {
        if (!['corian', 'both'].includes(map.get('material'))) {
          return undefined
        } else {
          return {
            thickness: (() => {
              const val = map.get('corian_thickness')
              if (val === 'other') {
                return parseInt(map.get('corian_thickness_custom'), 10)
              } else {
                return parseInt(val, 10)
              }
            })(),
            color: (() => {
              const name = map.get('corian_color') as string
              const warnings = getColorWarnings(name) || undefined

              return {name, warnings}
            })(),
            profile: map.get('corian_profile'),
            groove: (() => {
              const value = map.get('corian_groove')
              if (value === 'none' || value === undefined) {
                return undefined
              } else {
                return {
                  sort: value,
                  side: map.get('corian_groove_side'),
                }
              }
            })(),
            cavetto: (() => {
              const name = map.get('cavetto') as string
              const helpText = getCavettoHelpText(name) || undefined

              return {name, helpText}
            })(),
            cavetto_height: (() => {
              const height = map.get('cavetto_height')
              if (height !== undefined) {
                return parseInt(height)
              } else {
                return undefined
              }
            })(),
          }
        }
      })(),
      cooktop_mounting: (() => {
        const mounting = map.get('cooktop_mounting');
        if (mounting === 'none') {
          return undefined
        } else {
          return {
            value: mounting,
            description: map.get('cooktop_mounting_type'),
          }
        }
      })(),
      cooktop_steel_rods: (() => {
        let steel_rods = map.get('cooktop_steel_rods')
        if (steel_rods === 'none') {
          return undefined
        } else {
          return {
            sort: steel_rods,
            side: map.get('cooktop_steel_rods_side'),
          }
        }
      })(),
      el_tower_count: parseInt(map.get('el_tower_count'), 10),
      wood_charger_count: parseInt(map.get('wood_charger_count'), 10),
      corian_charger_count: parseInt(map.get('corian_charger_count'), 10),
      el_tower_type: map.get('el_tower_type'),
      corian_backflip_count: parseInt(map.get('corian_backflip_count'), 10),
      wood_backflip_count: parseInt(map.get('wood_backflip_count'), 10),
      basin: (() => {
        const type = map.get('basin');
        if (type === 'none') {
          return undefined
        } else {
          return {
            sort: type,
            color: map.get('basin_color'),
            mounting: map.get('basin_mounting'),
            mixer: map.get('basin_mounting_mixer'),
            mixer_diameter: parseInt(map.get('basin_mounting_mixer_type'), 10)
          }
        }
      })(),
      customer: {
        name: map.get('name'),
        phone: map.get('phone') || null,
        delivery_phone: (() => {
          if (map.get('delivery_phone_same')) {
            return null
          } else {
            return map.get('delivery_phone_alt') || null
          }
        })(),
        email: map.get('email') || null,
        address: map.get('address') || null,
        postal_number: map.get('postal_num'),
        postal_place: map.get('postal_place'),
        floor: parseInt(map.get('floor'), 10),
        elevator: map.get('elevator'),
      },
      delivery: {
        hull_date: format_date(map.get('hull_date')),
        countertop_date: format_date(map.get('countertop_date')),
        measurements: (() => {
          const measurements = map.get('measurements')
          if (measurements !== undefined) {
            if (measurements === 'later') {
              return {
                later: format_date(map.get('measurements_date')) || null
              }
            } else {
              return measurements
            }
          }

          if (map.get('measurements_corinor')) {
            return 'corinor'
          }

          return undefined
        })(),
        mounting: map.get('mounting'),
        lift: map.get('lift'),
        helper: map.get('helper'),
        wall_seam: (() => {
          const value = map.get('wall_seam')
          if (value === 'none') {
            return undefined
          } else if (value === 'other') {
            return map.get('wall_seam_other')
          } else {
            return value
          }
        })(),
        parking_distance: parseInt(map.get('parking'), 10),
        entrepreneur_name: map.get('entrepreneur_name') || null,
      },
      retailer: {
        name: map.get('retailer_person'),
        phone: map.get('retailer_phone'),
        email: map.get('retailer_email'),
        retailer: map.get('retailer_name'),
      },
      comments: map.get('comments') || '',
    };
  },
}

const actions: ActionTree<FormState, any> = {

  sendInquiry: ({commit, state, getters, rootState }): Promise<string> => {
    let data = new FormData()
    data.append('inquiry', JSON.stringify(getters.inquiry))

    let countertop_drawing = state.attachments['countertop_drawing']
    if (countertop_drawing) {
       data.append('countertop_drawing', countertop_drawing)
     }

    let floor_plan = state.attachments['floor_plan']
    if (floor_plan) {
       data.append('floor_plan', floor_plan)
     }

    let perspective_drawing = state.attachments['perspective_drawing']
    if (perspective_drawing) {
       data.append('perspective_drawing', perspective_drawing)
     }

    return axios.post(`${API_URL}/inquiry`, data, {
      headers: {
        'Access-Token': rootState.auth.token,
      },
    }).then(response => {
      commit('setChanged', false)
      return Promise.resolve(response.data.id)
    }).catch(error => {
      return Promise.reject(error)
    })
  },
}

const mutations: MutationTree<FormState> = {
  setFieldValue(state, { fieldName, value }) {
    let fieldIndex = state.fields.findIndex(field => field.name === fieldName)
    if (fieldIndex === -1) {
      state.fields.push(initField(fieldName))
      fieldIndex = state.fields.length - 1
    }

    Vue.set(state.fields, fieldIndex, {
      ...state.fields[fieldIndex],
      value,
    })

    if (Modernizr.localstorage && state.fields[fieldIndex].store) {
      window.localStorage.setItem(fieldName, value)
    }
    state.changed = true
  },
  setAttachment(state, { name, file }) {
    Vue.set(state.attachments, name, file)
  },
  setChanged(state, payload) {
    state.changed = payload
  }
}

let form: Module<FormState, any> = {
  namespaced: true,
  state: new FormState(),
  getters,
  actions,
  mutations,
}

export default form
